import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { blogPosts } from "./blogData";
import {
  LuActivity,
  LuArrowRight,
  LuBell,
  LuBot,
  LuCheck,
  LuChevronDown,
  LuClock,
  LuFileSearch,
  LuLayers,
  LuMapPin,
  LuRoute,
  LuQuote,
  LuRadar,
  LuSparkles,
  LuTarget,
  LuTrendingUp,
  LuTrophy,
  LuUsers,
  LuX,
  LuZap,
} from "react-icons/lu";
import PublicNav, { PublicFooter } from "../components/PublicNav";
import SEO from "../components/SEO";
import { Badge, MatchRing, initialsOf } from "../components/Ui";
import "./Landing.css";

/* ---------- content (ported from the Aspire home, adapted to CareerSavvy) ---------- */

/* Generic, illustrative sample roles — no employer names (these are examples of
   what a scored feed looks like, not real listings). */
const SAMPLE_JOBS = [
  { score: 92, title: "Senior Data Engineer", meta: "Remote · Contract" },
  { score: 84, title: "Cloud Solutions Architect", meta: "Hybrid · Contract" },
  { score: 71, title: "Full-Stack Developer", meta: "Onsite · Full-time" },
];

const MATCHED_SKILLS = ["Python", "SQL", "Airflow", "dbt"];
const MISSING_SKILLS = ["Terraform", "Kafka", "Spark"];

const STEPS = [
  {
    icon: LuFileSearch,
    title: "Tell CareerSavvy what you do",
    body: "Upload a resume once. CareerSavvy keeps the skills, seniority and role shapes that actually describe you.",
    bullets: ["Skills, seniority and years", "Roles you would say yes to", "One upload, no forms"],
  },
  {
    icon: LuRadar,
    title: "Read the score, not the listing",
    body: "Every opening arrives with a 0-100 score against your real background, so you skip the ones that were never a fit.",
    bullets: ["Ranked feed, strongest first", "Fresh contract and full-time roles daily", "Filters that narrow in one click"],
  },
  {
    icon: LuZap,
    title: "Close the gap before applying",
    body: "Matched and missing skills sit side by side, and one click rewrites your resume for the posting.",
    bullets: ["Matched skills to lead with", "Missing skills named plainly", "Tailored resume as a polished PDF"],
  },
  {
    icon: LuTarget,
    title: "Rehearse, then apply",
    body: "Aspire Quest and AI mock interviews turn the role into practice sessions with instant feedback.",
    bullets: ["Role-specific question sets", "Voice and video practice", "Feedback you can act on"],
  },
];

const SCORE_FACTORS = [
  { label: "Skill overlap", weight: 45, note: "How much of the required stack you already ship with." },
  { label: "Seniority fit", weight: 25, note: "Whether the level and years asked for line up with yours." },
  { label: "Workplace match", weight: 18, note: "Remote, hybrid or onsite against what you want." },
  { label: "Compensation", weight: 12, note: "How the posted range sits against your target." },
];

const LIVE_STATS = [
  { icon: LuTrendingUp, stat: "1,247", label: "matches made this week", change: "+12% vs last week" },
  { icon: LuClock, stat: "4.2 min", label: "average time from match to a tailored application", change: "-18% vs last week" },
  { icon: LuTarget, stat: "68%", label: "of users find a strong match in their first 10 jobs", change: "steady" },
  { icon: LuZap, stat: "3.1x", label: "more callbacks on strong-match applications", change: "+0.4x vs last month" },
  { icon: LuTrophy, stat: "42%", label: "faster interview prep with practice tracks", change: "+6% vs last month" },
  { icon: LuUsers, stat: "89%", label: "skill-gap clarity score after viewing a match", change: "+3% vs last week" },
];

const CHART_COLS = [42, 55, 48, 61, 58, 72, 68, 79, 74, 82, 88, 85, 91, 94];
const WORKPLACE_SPLIT = [
  { label: "Remote", value: 58 },
  { label: "Hybrid", value: 31 },
  { label: "On-site", value: 11 },
];

const FEATURES = [
  {
    icon: LuRoute,
    title: "Your AI Career Journey",
    body: "Tell your AI coach your goal in a quick chat and get a personalized, step-by-step roadmap — with a readiness score that climbs as you build proof, prep, and apply.",
  },
  {
    icon: LuRadar,
    title: "Scored feed, not a firehose",
    body: "Openings arrive ranked by fit with the score attached, so the first role you read is the one worth reading.",
  },
  {
    icon: LuZap,
    title: "Skill gaps in plain language",
    body: "Every match splits what you already bring from what the role wants next. No guessing which line to rewrite.",
  },
  {
    icon: LuLayers,
    title: "One-click tailored resumes",
    body: "Resume suggestions rewritten for each posting, exportable as a polished PDF in minutes.",
  },
  {
    icon: LuTrophy,
    title: "Aspire Quest practice tracks",
    body: "Text and video interview practice with timeboxes, model answers and scoring for every rep.",
  },
  {
    icon: LuBell,
    title: "Job alerts that respect fit",
    body: "Fresh contract and full-time roles land daily, matched to your profile before they reach your inbox.",
  },
  {
    icon: LuBot,
    title: "Prep tied to the role",
    body: "Mock interview questions map back to the skills the job asked for, so rehearsal is never generic filler.",
  },
  {
    icon: LuUsers,
    title: "Outreach from your inbox",
    body: "Upload a contact list and send personalized recruiter outreach from your own mailbox, so replies come straight to you.",
  },
  {
    icon: LuActivity,
    title: "Track every application",
    body: "Save, apply, interview, offer — keep every role you're pursuing on one board that moves as you do.",
  },
];

const CAREER_JOURNEY_STAGES = [
  { icon: LuTarget, stage: "Discover", title: "Where you stand", body: "A quick chat with your AI coach reads your skills and picks a target role that fits." },
  { icon: LuRoute, stage: "Plan", title: "Your roadmap", body: "A phased plan — foundations to offer — tailored to your experience and timeline." },
  { icon: LuZap, stage: "Do", title: "Work the steps", body: "Every step comes with why it matters, resources, and notes you can keep." },
  { icon: LuTrendingUp, stage: "Prove", title: "Get ready", body: "A readiness score that climbs as you build proof, close gaps, and practice." },
  { icon: LuTrophy, stage: "Land", title: "Get hired", body: "Apply to matched roles, rehearse interviews, and track every application to an offer." },
];

const OLD_WAY = [
  "Scrolling hundreds of listings that were never a fit",
  "Guessing why an application went quiet",
  "Rewriting the same resume for every posting",
  "Prepping for interviews the night before",
];

const NEW_WAY = [
  "A ranked feed where the top result is genuinely the best fit",
  "A 0-100 score on every job, computed from your resume",
  "Named skill gaps you can close or speak to",
  "Practice sessions that keep score long before the call",
];

const TESTIMONIALS = [
  {
    name: "Maya Rios",
    role: "Frontend engineer, mid-level",
    hue: 168,
    quote:
      "I stopped applying to everything. The score told me which six roles were actually worth a tailored resume, and two of them called back.",
  },
  {
    name: "Devin Okafor",
    role: "Career switcher, ex-analyst",
    hue: 220,
    quote:
      "Seeing the missing skills listed out was blunt in a useful way. I knew exactly what to learn before I wasted another month.",
  },
  {
    name: "Priya Nandini",
    role: "Product engineer",
    hue: 300,
    quote:
      "The mock interviews are the part I keep going back to. Timed questions with instant feedback beats rereading interview blog posts.",
  },
];

const QUEST_TRACKS = [
  { name: "Behavioral", tagline: "Stories, structure and delivery", count: "Guided sessions" },
  { name: "Technical screen", tagline: "Fundamentals under a timebox", count: "Code submissions" },
  { name: "Video interview", tagline: "Camera-on practice with review", count: "Recorded reps" },
  { name: "Role-specific", tagline: "Built from the job description", count: "Tailored sets" },
];

const FAQ = [
  [
    "How is the match score calculated?",
    "It weighs overlapping skills, seniority fit, workplace preference and compensation target against your resume. Each job page shows the reasoning in plain language.",
  ],
  [
    "Do I need an account?",
    "Yes, and it is free to start. Your resume powers the matching, so a quick signup and one upload is all it takes to see your first scored feed.",
  ],
  [
    "Are these real job listings?",
    "Yes. CareerSavvy pulls fresh contract and full-time openings daily from across the major job platforms and scores each one against your profile.",
  ],
  [
    "What is Aspire Quest?",
    "A structured interview practice mode: behavioral, technical and video tracks with timeboxes, model answers and scoring, plus AI mock interviews tied to your target role.",
  ],
  [
    "Can CareerSavvy tailor my resume to a job?",
    "Yes. Pick a match or bring a job description and CareerSavvy generates an ATS-friendly, job-specific resume in seconds — your original resume stays untouched.",
  ],
  [
    "Will my resume pass applicant tracking systems (ATS)?",
    "Yes. Resumes use a clean, single-column, parser-safe layout with standard section headings that ATS software reads reliably.",
  ],
  [
    "Does CareerSavvy work outside the US?",
    "Yes. CareerSavvy supports both the US and India job markets and shows market-appropriate roles based on the country on your profile.",
  ],
  [
    "Is my data private and secure?",
    "Your resume and account data are used only to power your matches and coaching. Our Privacy Policy explains what we collect, how we use it, and your rights (including GDPR).",
  ],
  [
    "Is it free, and can I cancel anytime?",
    "You can start for free. Pro unlocks match analysis, tailored resumes and voice interviews — and you can cancel your subscription at any time.",
  ],
];

/* ---------- reveal-on-scroll ---------- */

function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => el.classList.add("ld-in"), delay);
            io.disconnect();
          }
        });
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [delay]);
  return (
    <div ref={ref} className={`ld-reveal ${className}`}>
      {children}
    </div>
  );
}

function SampleJobCard({ job }) {
  return (
    <div className="card card-pad row ld-tile-hover" style={{ gap: 14, borderRadius: 18, alignItems: "center" }}>
      <MatchRing score={job.score} />
      <div className="grow" style={{ minWidth: 0 }}>
        <div className="t-h3" style={{ lineHeight: 1.25 }}>{job.title}</div>
        <div className="row t-sm mt-1" style={{ gap: 5, color: "var(--ink-3)" }}>
          <LuMapPin size={13} /> {job.meta}
        </div>
      </div>
      <Badge tone={job.score >= 80 ? "accent" : "amber"}>{job.score >= 80 ? "Strong match" : "Good match"}</Badge>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`ld-faq${open ? " open" : ""}`}>
      <button type="button" className="ld-faq-q" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        {q}
        <LuChevronDown size={17} className="ld-faq-chev" />
      </button>
      {open ? <div className="ld-faq-a">{a}</div> : null}
    </div>
  );
}

/* ---------- page ---------- */

export default function Landing() {
  const top = SAMPLE_JOBS[0];
  return (
    <div className="csx">
      <SEO
        description="CareerSavvy is your AI-powered job-search assistant: personalised job matches, AI resume tailoring, mock interviews and interview prep. Start free."
        canonicalPath="/"
      />
      <div className="csx-page">
        <PublicNav />

        {/* HERO */}
        <section className="ld-hero">
          <div className="ld-mesh" aria-hidden />
          <div className="ld-grid-faint" aria-hidden />
          <div className="ld-glow" aria-hidden />

          <div className="ld-hero-inner">
            <span className="ld-kicker">
              <LuSparkles size={14} />
              Match, prep, apply: in one place
            </span>
            <h1 className="ld-h1">
              Fewer applications.
              <br />
              <span className="text-mint">Far better odds.</span>
            </h1>
            <p className="ld-sub">
              CareerSavvy reads your resume, scores every opening against it, and shows exactly which skills close
              the gap. Then it drills you on the interview before you ever hit apply.
            </p>
            <div className="ld-cta-row">
              <Link to="/page-register" className="btn btn-lg btn-pill btn-mint">
                Get started free <LuArrowRight size={17} />
              </Link>
              <Link to="/login" className="btn btn-lg btn-pill btn-glass">
                Sign in
              </Link>
            </div>
          </div>

          {/* BENTO */}
          <Reveal>
            <div className="ld-bento">
              <div className="ld-glass ld-tile-hover ld-col-2" style={{ padding: 32, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <MatchRing score={top.score} size={120} />
                <p className="t-h3 mt-3 t-center">{top.title}</p>
                <p className="t-sm t-center">{top.meta}</p>
              </div>

              <div className="ld-glass ld-tile-hover ld-col-4" style={{ padding: 20 }}>
                <p className="ld-eyebrow mb-2">Live from your feed</p>
                <div className="stack-2">
                  {SAMPLE_JOBS.slice(0, 2).map((j) => (
                    <SampleJobCard key={j.title} job={j} />
                  ))}
                </div>
              </div>

              <div className="ld-glass ld-tile-hover ld-col-3" style={{ padding: 28 }}>
                <LuZap size={20} style={{ color: "var(--accent)" }} />
                <h3 className="t-h2 mt-2">Skill gaps, named out loud</h3>
                <p className="t-sm mt-1">Each match separates what you already have from what the role wants next.</p>
                <div className="row mt-3" style={{ flexWrap: "wrap", gap: 8 }}>
                  {MATCHED_SKILLS.map((s) => (
                    <span key={s} className="ld-chip-on">{s}</span>
                  ))}
                  {MISSING_SKILLS.map((s) => (
                    <span key={s} className="ld-chip-off">{s}</span>
                  ))}
                </div>
              </div>

              <div className="ld-glass ld-tile-hover ld-col-3" style={{ padding: 28, position: "relative", overflow: "hidden" }}>
                <div
                  aria-hidden
                  style={{
                    position: "absolute", right: -64, top: -64, width: 208, height: 208, borderRadius: "50%",
                    background: "var(--accent)", opacity: 0.16, filter: "blur(64px)",
                  }}
                />
                <LuTrophy size={20} style={{ color: "var(--accent)" }} />
                <h3 className="t-h2 mt-2">Practice that keeps score</h3>
                <p className="t-sm mt-1">Aspire Quest and AI mock interviews with feedback on every rep.</p>
                <Link to="/page-register" className="row mt-3 t-sm" style={{ gap: 6, color: "var(--accent)", fontWeight: 600 }}>
                  Start practising <LuArrowRight size={15} />
                </Link>
              </div>

              <div className="ld-glass ld-col-6" style={{ padding: 28 }}>
                <div className="grid-4" style={{ gap: 24 }}>
                  {[
                    ["Daily", "fresh contract and full-time roles, scored for you"],
                    ["0-100", "match score with the reasoning behind it"],
                    ["1-click", "tailored resume for every application"],
                    ["AI", "mock interviews with instant feedback"],
                  ].map(([stat, label]) => (
                    <div key={label}>
                      <p className="ld-stat-n text-mint" style={{ fontSize: 30 }}>{stat}</p>
                      <p className="t-sm mt-1">{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </section>

        {/* CAREER JOURNEY SPOTLIGHT */}
        <section className="ld-section">
          <Reveal>
            <span className="ld-kicker">New · AI-first</span>
            <h2 className="ld-h2 mt-3">Your AI Career Journey</h2>
            <p className="t-body mt-2 measure">
              Don't just find a job — follow a plan to land it. Tell your AI coach your goal in a quick chat and get a
              personalized, phase-by-phase roadmap, with a readiness score that climbs as you build proof, prep, and apply.
            </p>
          </Reveal>
          <div
            className="mt-4"
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(165px, 1fr))", gap: 16, alignItems: "stretch" }}
          >
            {CAREER_JOURNEY_STAGES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={i * 80} className="grow">
                  <div className="card ld-tile-hover" style={{ borderRadius: 20, padding: 22, height: "100%" }}>
                    <span className="ld-step-icon"><Icon size={20} /></span>
                    <div className="ld-eyebrow mt-2">{s.stage}</div>
                    <h3 className="t-h3 mt-1" style={{ fontSize: 18 }}>{s.title}</h3>
                    <p className="t-sm mt-1">{s.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal>
            <Link to="/page-register" className="btn btn-mint btn-pill mt-4">
              Start your journey <LuArrowRight size={16} />
            </Link>
          </Reveal>
        </section>

        {/* HOW IT WORKS */}
        <section className="ld-section">
          <Reveal>
            <span className="ld-kicker">How it works</span>
            <h2 className="ld-h2 mt-3">The CareerSavvy loop</h2>
            <p className="t-body mt-2 measure">Four moves that replace spraying applications and hoping something lands.</p>
          </Reveal>
          <div className="grid-4 mt-4" style={{ gap: 16, alignItems: "stretch" }}>
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={i * 90} className="grow">
                  <div className="card ld-tile-hover" style={{ borderRadius: 24, padding: 28, height: "100%" }}>
                    <div className="row" style={{ gap: 12 }}>
                      <span className="ld-step-icon"><Icon size={20} /></span>
                      <span className="ld-eyebrow">Step {i + 1}</span>
                    </div>
                    <h3 className="t-h3 mt-3">{s.title}</h3>
                    <p className="t-sm mt-1">{s.body}</p>
                    <ul className="stack-2 mt-3" style={{ borderTop: "1px solid var(--line)", paddingTop: 14 }}>
                      {s.bullets.map((b) => (
                        <li key={b} className="row t-sm" style={{ gap: 8, alignItems: "flex-start" }}>
                          <LuCheck size={14} style={{ color: "var(--accent)", marginTop: 3, flex: "0 0 auto" }} />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>

        {/* SCORE BREAKDOWN */}
        <section className="ld-band">
          <div className="ld-section grid-2" style={{ gap: 40, alignItems: "center" }}>
            <Reveal>
              <h2 className="ld-h2">What goes into a match score</h2>
              <p className="t-body mt-3 measure">
                No black box. Each score is a weighted read of what CareerSavvy can actually see in your resume, and
                every job page repeats the reasoning in words before you decide to apply.
              </p>
              <Link to="/page-register" className="btn btn-ghost btn-pill mt-4">
                See your scored feed <LuArrowRight size={16} />
              </Link>
            </Reveal>
            <Reveal delay={90}>
              <div className="ld-glass stack-3" style={{ padding: 28 }}>
                {SCORE_FACTORS.map((f) => (
                  <div key={f.label}>
                    <div className="row-between">
                      <span className="t-sm t-ink" style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>{f.label}</span>
                      <span className="t-sm" style={{ color: "var(--accent)" }}>{f.weight}%</span>
                    </div>
                    <div className="ld-bar mt-1"><span style={{ width: `${f.weight * 2}%` }} /></div>
                    <p className="t-xs mt-1">{f.note}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* LIVE STATS DASHBOARD */}
        <section className="ld-section">
          <Reveal>
            <div className="row-between" style={{ flexWrap: "wrap", gap: 16, alignItems: "flex-end" }}>
              <div>
                <span className="ld-kicker"><LuActivity size={14} /> Live platform stats</span>
                <h2 className="ld-h2 mt-3">CareerSavvy by the numbers</h2>
                <p className="t-body mt-2 measure">The shape of a busy week when matching is fast and prep is tied to the role.</p>
              </div>
              <span className="ld-pulse"><span className="ld-dot" /> Updated live</span>
            </div>
          </Reveal>
          <div className="grid-3 mt-4" style={{ gap: 16 }}>
            {LIVE_STATS.map((card, i) => {
              const Icon = card.icon;
              return (
                <Reveal key={card.label} delay={i * 70}>
                  <div className="ld-glass ld-tile-hover" style={{ padding: 28, height: "100%" }}>
                    <div className="row-between" style={{ alignItems: "flex-start" }}>
                      <span className="ld-stat-icon"><Icon size={20} /></span>
                      <span className="ld-change">{card.change}</span>
                    </div>
                    <p className="ld-stat-n text-mint mt-4">{card.stat}</p>
                    <p className="t-sm mt-1">{card.label}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={120}>
            <div className="grid-2 mt-3" style={{ gap: 16 }}>
              <div className="ld-glass" style={{ padding: 28 }}>
                <div className="row-between">
                  <h3 className="t-h3" style={{ fontFamily: "var(--font-display)" }}>Match quality over time</h3>
                  <span className="t-xs">Last 14 days</span>
                </div>
                <div className="ld-cols">
                  {CHART_COLS.map((h, i) => (
                    <span key={i} style={{ height: `${h}%` }} aria-hidden />
                  ))}
                </div>
                <div className="row-between mt-2 t-xs"><span>Jul 16</span><span>Jul 30</span></div>
              </div>
              <div className="ld-glass" style={{ padding: 28 }}>
                <div className="row-between">
                  <h3 className="t-h3" style={{ fontFamily: "var(--font-display)" }}>Where the strong matches live</h3>
                  <span className="t-xs">By workplace</span>
                </div>
                <div className="stack-3 mt-4">
                  {WORKPLACE_SPLIT.map((bar) => (
                    <div key={bar.label}>
                      <div className="row-between t-sm"><span className="t-ink">{bar.label}</span><span>{bar.value}%</span></div>
                      <div className="ld-bar mt-1" style={{ height: 8 }}><span style={{ width: `${bar.value}%` }} /></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </section>

        {/* TOP MATCHES */}
        <section className="ld-band">
          <div className="ld-section">
            <Reveal>
              <div className="row-between" style={{ flexWrap: "wrap", gap: 16, alignItems: "flex-end" }}>
                <div>
                  <h2 className="ld-h2">Today's strongest matches</h2>
                  <p className="t-body mt-2">Scored against a sample engineering profile.</p>
                </div>
                <Link to="/page-register" className="btn btn-ghost btn-pill">
                  Browse all matches <LuArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
            <div className="grid-3 mt-4" style={{ gap: 16 }}>
              {SAMPLE_JOBS.map((j, i) => (
                <Reveal key={j.title} delay={i * 80}>
                  <SampleJobCard job={j} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* FEATURES */}
        <section className="ld-section">
          <Reveal>
            <h2 className="ld-h2">Everything the search actually needs</h2>
            <p className="t-body mt-2 measure">
              Not a job board with a chatbot bolted on. Each piece exists because something in the hunt was broken.
            </p>
          </Reveal>
          <div className="grid-3 mt-4" style={{ gap: 16 }}>
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.title} delay={i * 70}>
                  <div className="ld-glass ld-tile-hover" style={{ padding: 28, height: "100%" }}>
                    <span className="ld-stat-icon"><Icon size={20} /></span>
                    <h3 className="t-h3 mt-3">{f.title}</h3>
                    <p className="t-sm mt-1">{f.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>

        {/* COMPARISON */}
        <section className="ld-band">
          <div className="ld-section" style={{ maxWidth: 920 }}>
            <Reveal>
              <h2 className="ld-h2">The old way vs. the CareerSavvy way</h2>
            </Reveal>
            <div className="grid-2 mt-4" style={{ gap: 16 }}>
              <Reveal>
                <div className="ld-old">
                  <p className="ld-eyebrow">Spray and pray</p>
                  <ul className="stack-2 mt-3">
                    {OLD_WAY.map((t) => (
                      <li key={t} className="row t-sm" style={{ gap: 10, alignItems: "flex-start" }}>
                        <LuX size={15} style={{ opacity: 0.6, marginTop: 3, flex: "0 0 auto" }} />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
              <Reveal delay={90}>
                <div className="ld-new ld-tile-hover">
                  <p className="ld-eyebrow" style={{ color: "var(--accent)" }}>With CareerSavvy</p>
                  <ul className="stack-2 mt-3">
                    {NEW_WAY.map((t) => (
                      <li key={t} className="row t-sm t-ink" style={{ gap: 10, alignItems: "flex-start" }}>
                        <LuCheck size={15} style={{ color: "var(--accent)", marginTop: 3, flex: "0 0 auto" }} />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="ld-section">
          <Reveal>
            <h2 className="ld-h2">Notes from people mid-search</h2>
          </Reveal>
          <div className="grid-3 mt-4" style={{ gap: 16 }}>
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 80}>
                <figure className="ld-glass ld-tile-hover" style={{ padding: 28, height: "100%", display: "flex", flexDirection: "column" }}>
                  <LuQuote size={20} style={{ color: "var(--accent)" }} />
                  <blockquote className="t-sm t-ink grow mt-3" style={{ lineHeight: 1.6 }}>{t.quote}</blockquote>
                  <figcaption className="row mt-4" style={{ gap: 12 }}>
                    <span
                      className="avatar"
                      style={{ width: 38, height: 38, background: `oklch(0.35 0.08 ${t.hue})`, color: `oklch(0.9 0.06 ${t.hue})` }}
                    >
                      {initialsOf(t.name)}
                    </span>
                    <span>
                      <span className="t-sm t-ink" style={{ display: "block", fontWeight: 600 }}>{t.name}</span>
                      <span className="t-xs" style={{ display: "block" }}>{t.role}</span>
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </section>

        {/* QUEST CTA */}
        <section className="ld-section" style={{ paddingTop: 0 }}>
          <Reveal>
            <div className="ld-quest">
              <div className="ld-mesh" aria-hidden />
              <div className="ld-quest-inner">
                <div>
                  <span className="ld-kicker"><LuTrophy size={14} /> Aspire Quest</span>
                  <h2 className="ld-h2 mt-3">Interview practice that keeps score</h2>
                  <p className="t-body mt-3 measure">
                    Practice tracks with timeboxes, model answers and scoring, plus AI mock interviews that talk back.
                    Rate yourself, bank the progress, keep the streak.
                  </p>
                  <Link to="/page-register" className="btn btn-lg btn-pill btn-mint mt-4">
                    Start a practice session <LuArrowRight size={17} />
                  </Link>
                </div>
                <ul className="grid-2" style={{ gap: 12 }}>
                  {QUEST_TRACKS.map((t) => (
                    <li key={t.name} className="ld-track ld-tile-hover">
                      <LuBot size={20} style={{ color: "var(--accent)" }} />
                      <p className="t-h3 mt-2" style={{ fontFamily: "var(--font-display)" }}>{t.name}</p>
                      <p className="t-sm mt-1">{t.tagline}</p>
                      <p className="ld-eyebrow mt-3" style={{ letterSpacing: "0.12em" }}>{t.count}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </section>

        {/* FAQ */}
        <section className="ld-section" style={{ maxWidth: 760, paddingTop: 0, paddingBottom: 96 }}>
          <Reveal>
            <h2 className="ld-h2">Questions people ask</h2>
            <div className="mt-3" style={{ borderTop: "1px solid var(--line)" }}>
              {FAQ.map(([q, a]) => (
                <FaqItem key={q} q={q} a={a} />
              ))}
            </div>
          </Reveal>
        </section>

        {/* Articles — locally authored, no network call */}
        <section className="ld-section" style={{ paddingTop: 0 }}>
          <Reveal>
            <div className="ld-head-row">
              <div>
                <h2 className="ld-h2">From the CareerSavvy library</h2>
                <p className="ld-sub mt-1">
                  Background reading on how hiring processes, applicant tracking systems and student work
                  authorisation are generally structured.
                </p>
              </div>
              <Link className="ld-head-link" to="/blog">
                All articles <LuArrowRight size={15} />
              </Link>
            </div>
            <div className="ld-articles mt-4">
              {blogPosts.slice(0, 6).map((post) => (
                <Link key={post.id} className="ld-article" to={`/blogDetailed?slug=${post.id}`}>
                  <span className="ld-article-tag">{post.audience}</span>
                  <h3 className="ld-article-title">{post.title}</h3>
                  <p className="ld-article-desc">{post.description}</p>
                  <span className="ld-article-meta">{post.readTime}</span>
                </Link>
              ))}
            </div>
          </Reveal>
        </section>

        <PublicFooter />
      </div>
    </div>
  );
}
